import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, InputAdornment, List, ListItemButton, ListItemText, Snackbar, Stack,
  Tab, Tabs, TextField, Table, TableBody, TableCell, TableHead, TableRow, ToggleButton, ToggleButtonGroup,
  Tooltip, Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RefreshIcon from "@mui/icons-material/Refresh";
import { AppLayout } from "@/components/AppLayout";
import {
  billPageState, billPdfUrl, blockingCandidates, createBillDraft, deriveProjectCoding, fetchBillDraft, isDraftSettling,
  possibleDuplicateFrom, refreshPoStatus, submitBillDraft, updateBillDraft,
  type BillDraftContent, type BillLine, type BillPageState, type DuplicateCandidate, type DuplicateCheckStatus,
  type PoStatus, type ProjectCoding, type VendorBillDraft,
} from "@/api/bills";
import { DuplicateCompareDialog } from "@/components/DuplicateCompareDialog";
import { RemindMeButton } from "@/components/RemindMeButton";
import {
  fetchProjectsByIds, fetchReferenceData, fetchVendorsByIds, searchProjects, searchVendors,
  type ItemOption, type ProjectOption, type RefOption, type ReferenceData, type VendorOption,
} from "@/api/referenceData";

const SETTLING_LABEL: Record<string, string> = {
  created: "Starting…",
  ocr_running: "Reading the PDF…",
  ocr_done: "PDF read — structuring the bill…",
  structuring: "Structuring the bill with Claude…",
};

const USD = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
const fmtUsd = (n: number | null | undefined) => (n == null || Number.isNaN(Number(n)) ? "" : USD.format(Number(n)));

export function CreateBillPage() {
  const { id } = useParams();
  const draftId = Number(id);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ["bill_draft", draftId],
    queryFn: () => fetchBillDraft(draftId),
    refetchInterval: (q) => (q.state.data && isDraftSettling(q.state.data) ? 2000 : false),
  });
  const reference = useQuery({ queryKey: ["reference_data"], queryFn: fetchReferenceData });

  const draft = query.data;
  // Set by the "Draft bill" buttons when the server handed back an existing
  // posted or failed draft for this invoice instead of starting a second one.
  const existingDraft = (useLocation().state as BillPageState | null)?.existingDraft;
  const startOver = useMutation({
    mutationFn: () => createBillDraft(
      draft!.triage_decision_id as number, draft!.source_attachment_id ?? undefined,
      draft!.source_page_start != null && draft!.source_page_end != null
        ? { start: draft!.source_page_start, end: draft!.source_page_end } : undefined,
      true,
    ),
    onSuccess: (fresh) => navigate(`/bills/${fresh.id}`, { replace: true, state: billPageState(fresh) }),
  });

  return (
    <AppLayout maxWidth={false}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>
        Back
      </Button>
      {draft && existingDraft === "posted" && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This invoice was already posted to NetSuite from this email, so the existing bill was opened instead of
          creating a second one.
        </Alert>
      )}
      {draft && existingDraft === "failed" && (
        <Alert severity="info" sx={{ mb: 2 }}
          action={draft.triage_decision_id != null ? (
            <Button color="inherit" size="small" onClick={() => startOver.mutate()} disabled={startOver.isPending}>
              {startOver.isPending ? "Starting…" : "Start a new draft"}
            </Button>
          ) : undefined}>
          A previous draft for this invoice failed. It was reopened so you can fix and retry it rather than
          starting a duplicate. Start a new draft only if this one can't be recovered (for example, the PDF couldn't be read).
        </Alert>
      )}
      {!draft ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}><CircularProgress /></Box>
      ) : (
        <BillEditor
          draft={draft}
          reference={reference.data}
          onChanged={() => qc.invalidateQueries({ queryKey: ["bill_draft", draftId] })}
        />
      )}
    </AppLayout>
  );
}

function BillEditor({ draft, reference, onChanged }: {
  draft: VendorBillDraft; reference?: ReferenceData; onChanged: () => void;
}) {
  const settling = isDraftSettling(draft);
  const containerRef = useRef<HTMLDivElement>(null);
  const [leftPct, setLeftPct] = useState(48);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e: MouseEvent) => {
      const r = containerRef.current?.getBoundingClientRect();
      if (!r) return;
      setLeftPct(Math.min(75, Math.max(25, ((e.clientX - r.left) / r.width) * 100)));
    };
    const onUp = () => setDragging(false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
  }, [dragging]);

  return (
    <Box
      ref={containerRef}
      sx={{ display: "flex", alignItems: "stretch", height: "82vh", width: "100%", overflow: "hidden",
            userSelect: dragging ? "none" : "auto" }}
    >
      <Box sx={{ width: `${leftPct}%`, minWidth: 0, border: "1px solid", borderColor: "divider", borderRadius: 1, overflow: "hidden",
                 display: "flex", flexDirection: "column" }}>
        {draft.original_url && (
          // The PDF below was rendered from the vendor's spreadsheet/document.
          <Typography variant="caption" color="text.secondary" sx={{ px: 1, py: 0.5, borderBottom: "1px solid", borderColor: "divider" }}>
            Converted from {draft.original_filename ?? "the vendor's file"}.{" "}
            <a href={draft.original_url}>Download original</a>
          </Typography>
        )}
        {draft.pdf_url ? (
          // pointer-events off while dragging so the PDF object doesn't swallow mousemove.
          <object data={billPdfUrl(draft.id)} type="application/pdf" width="100%" height="100%" aria-label="Invoice PDF"
            style={{ pointerEvents: dragging ? "none" : "auto", flex: 1, minHeight: 0 }}>
            <Typography sx={{ p: 2 }}>PDF preview unavailable. <a href={billPdfUrl(draft.id)}>Download</a></Typography>
          </object>
        ) : (
          <Box sx={{ p: 2 }}><Typography color="text.secondary">No PDF attached.</Typography></Box>
        )}
      </Box>

      {/* Draggable divider to resize the two panes. */}
      <Box
        onMouseDown={() => setDragging(true)}
        role="separator" aria-orientation="vertical" aria-label="Resize panes"
        sx={{
          flex: "0 0 10px", mx: 0.5, cursor: "col-resize", borderRadius: 1,
          bgcolor: dragging ? "primary.main" : "divider",
          "&:hover": { bgcolor: "primary.light" },
        }}
      />

      <Box sx={{ flex: 1, minWidth: 0, overflowY: "auto", overflowX: "hidden", pr: 1, pb: 3 }}>
        {settling ? (
          <Stack alignItems="center" spacing={2} sx={{ mt: 8 }}>
            <CircularProgress />
            <Typography color="text.secondary">{SETTLING_LABEL[draft.status] ?? "Working…"}</Typography>
          </Stack>
        ) : (
          // A failed submit is recoverable: keep the editable form (and its
          // "Post bill to NetSuite" button) so a reviewer can fix and retry,
          // with the last error surfaced above it. Rendering only the alert
          // left failed drafts a dead-end with no way to resubmit.
          <>
            {draft.status === "failed" && (
              <Alert severity="error" sx={{ mb: 2 }}>Draft failed: {draft.error_message}</Alert>
            )}
            <DraftForm draft={draft} reference={reference} onChanged={onChanged} />
          </>
        )}
      </Box>
    </Box>
  );
}

// A currency-formatted numeric field: shows $XX,XXX.XX when idle, raw number when editing.
function CurrencyField({ label, value, onChange, width }: {
  label?: string; value: number | string | null | undefined; onChange: (n: number | null) => void; width?: number;
}) {
  const [focused, setFocused] = useState(false);
  const [raw, setRaw] = useState("");
  const numeric = value == null || value === "" ? null : Number(value);
  const display = focused ? raw : fmtUsd(numeric);

  return (
    <TextField
      variant="standard" label={label} value={display} sx={{ width }}
      inputProps={{ inputMode: "decimal", style: { textAlign: "right" } }}
      onFocus={() => { setFocused(true); setRaw(numeric == null ? "" : String(numeric)); }}
      onChange={(e) => setRaw(e.target.value)}
      onBlur={() => {
        setFocused(false);
        const n = parseFloat(raw.replace(/[^0-9.-]/g, ""));
        onChange(Number.isNaN(n) ? null : n);
      }}
    />
  );
}

// Autocomplete over NetSuite reference options; value is the internal id, label is the name.
function RefSelect({ label, options, value, onChange, helperText, required }: {
  label: string; options: RefOption[]; value: number | string | null | undefined;
  onChange: (id: number | null) => void; helperText?: string; required?: boolean;
}) {
  const selected = options.find((o) => String(o.id) === String(value ?? "")) ?? null;
  return (
    <Autocomplete
      size="small" fullWidth options={options} value={selected}
      getOptionLabel={(o) => o.name ?? ""}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      onChange={(_, v) => onChange(v ? v.id : null)}
      renderInput={(p) => <TextField {...p} label={label} helperText={helperText} required={required} />}
    />
  );
}

// A compact, label-less select for per-line Department / Class / Location.
// alignEnd right-aligns the popup so it overflows to the LEFT of the input —
// used for the right-edge columns so the (wider) list stays on screen.
function CompactRef({ options, value, onChange, placeholder, alignEnd }: {
  options: RefOption[]; value: number | string | null | undefined;
  onChange: (id: number | null) => void; placeholder: string; alignEnd?: boolean;
}) {
  const selected = options.find((o) => String(o.id) === String(value ?? "")) ?? null;
  return (
    <Autocomplete
      size="small" options={options} value={selected}
      getOptionLabel={(o) => o.name ?? ""}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      onChange={(_, v) => onChange(v ? v.id : null)}
      renderInput={(p) => <TextField {...p} variant="standard" placeholder={placeholder} />}
      slotProps={{
        // Widen the popper itself (MUI sizes it to the tiny input) and, for the
        // right-edge columns, pin its right edge to the input so the list opens
        // to the LEFT instead of overflowing off-screen to the right.
        popper: alignEnd
          ? { placement: "bottom-end", style: { width: 240 } }
          : { placement: "bottom-start", style: { width: 240 } },
      }}
    />
  );
}

// One line-items or expenses table, with per-line dept/class/location overrides.
// Horizontally scrollable when the columns don't fit.
function LinesTable({ kind, rows, reference, items, setLine, removeLine, projectCache, registerProjects, onLineProject }: {
  kind: "item" | "expense";
  rows: { line: BillLine; index: number }[];
  reference?: ReferenceData;
  items: ItemOption[];
  setLine: (i: number, k: keyof BillLine, v: string | number | null) => void;
  removeLine: (i: number) => void;
  projectCache: Map<string, ProjectOption>;
  registerProjects: (opts: ProjectOption[]) => void;
  onLineProject: (i: number, opt: ProjectOption | null) => void;
}) {
  const departments = reference?.departments ?? [];
  const classes = reference?.classes ?? [];
  const locations = reference?.locations ?? [];
  const accounts = reference?.accounts ?? [];
  const isItem = kind === "item";

  return (
    <Box sx={{
      overflowX: "auto",
      "&::-webkit-scrollbar": { height: 8 },
      "&::-webkit-scrollbar-thumb": { bgcolor: "grey.400", borderRadius: 4 },
      "&::-webkit-scrollbar-thumb:hover": { bgcolor: "grey.600" },
    }}>
      <Table size="small" sx={{
        minWidth: isItem ? 1330 : 1110,
        "& td, & th": { px: 0.75, py: 0.5 },
        "& th": { fontSize: 12, fontWeight: 600, color: "text.secondary" },
        // Uniform input text size across every column.
        "& .MuiInputBase-input": { fontSize: 13 },
      }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ minWidth: isItem ? 200 : 240 }}>{isItem ? "Item" : "Account"}</TableCell>
            <TableCell sx={{ minWidth: 200 }}>Description</TableCell>
            {isItem && <TableCell align="right" sx={{ width: 56 }}>Qty</TableCell>}
            {isItem && <TableCell align="right" sx={{ width: 96 }}>Rate</TableCell>}
            <TableCell align="right" sx={{ width: 104 }}>Amount</TableCell>
            <TableCell sx={{ width: 130 }}>Dept</TableCell>
            <TableCell sx={{ width: 130 }}>Class</TableCell>
            <TableCell sx={{ width: 130 }}>Location</TableCell>
            <TableCell sx={{ width: 150 }}>Project</TableCell>
            <TableCell sx={{ width: 32 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(({ line, index }) => (
            <TableRow key={index} sx={{
              "& td": { verticalAlign: "top", pt: 1 },
              transition: "background-color 120ms",
              "&:hover": { bgcolor: "action.hover" },
              // Highlight the row currently being edited (any input focused).
              "&:focus-within": { bgcolor: "action.selected" },
            }}>
              <TableCell>
                {isItem
                  ? <ItemPicker items={items} value={line.item_id} onChange={(v) => setLine(index, "item_id", v)} />
                  : <CompactRef options={accounts} value={line.account_id} placeholder="Select account…" onChange={(v) => setLine(index, "account_id", v)} />}
              </TableCell>
              <TableCell>
                <TextField variant="standard" fullWidth multiline maxRows={6} value={line.description ?? ""}
                  onChange={(e) => setLine(index, "description", e.target.value)} />
              </TableCell>
              {isItem && (
                <TableCell align="right">
                  <TextField variant="standard" value={line.quantity ?? ""} inputProps={{ style: { textAlign: "right" } }}
                    onChange={(e) => setLine(index, "quantity", e.target.value === "" ? null : Number(e.target.value))} />
                </TableCell>
              )}
              {isItem && (
                <TableCell align="right"><CurrencyField value={line.rate} onChange={(n) => setLine(index, "rate", n)} width={90} /></TableCell>
              )}
              <TableCell align="right"><CurrencyField value={line.amount} onChange={(n) => setLine(index, "amount", n)} width={96} /></TableCell>
              <TableCell><CompactRef alignEnd options={departments} value={line.department_id} placeholder="—" onChange={(v) => setLine(index, "department_id", v)} /></TableCell>
              <TableCell><CompactRef alignEnd options={classes} value={line.class_id} placeholder="—" onChange={(v) => setLine(index, "class_id", v)} /></TableCell>
              <TableCell><CompactRef alignEnd options={locations} value={line.location_id} placeholder="—" onChange={(v) => setLine(index, "location_id", v)} /></TableCell>
              <TableCell>
                <ProjectPicker value={line.customer_id} cache={projectCache} onResolve={registerProjects}
                  onChange={(_, opt) => onLineProject(index, opt)} placeholder="—" />
              </TableCell>
              <TableCell>
                <IconButton size="small" aria-label="Remove line" onClick={() => removeLine(index)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && (
            <TableRow><TableCell colSpan={10}><Typography variant="caption" color="text.secondary">No {isItem ? "item" : "expense"} lines.</Typography></TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );
}

// Item cell: shows the selected item name and opens a wide modal picker on click.
function ItemPicker({ items, value, onChange }: {
  items: ItemOption[]; value: number | string | null | undefined; onChange: (id: number | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const current = items.find((i) => String(i.id) === String(value ?? ""));

  return (
    <>
      <Button
        variant="outlined" size="small" fullWidth onClick={() => setOpen(true)}
        sx={{ textTransform: "none", justifyContent: "flex-start", fontSize: 13, color: current ? "text.primary" : "text.secondary", borderColor: "divider" }}
      >
        <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
          {current ? current.name : "Select item…"}
        </Box>
      </Button>
      <ItemPickerDialog
        open={open} items={items} value={value}
        onClose={() => setOpen(false)}
        onSelect={(id) => { onChange(id); setOpen(false); }}
      />
    </>
  );
}

// Modal item picker: tabs per item type + search + scrollable name list.
function ItemPickerDialog({ open, items, value, onClose, onSelect }: {
  open: boolean; items: ItemOption[]; value: number | string | null | undefined;
  onClose: () => void; onSelect: (id: number | null) => void;
}) {
  // Tabs: "All" plus each type present, with counts.
  const tabs = useMemo(() => {
    const counts = new Map<string, number>();
    items.forEach((i) => counts.set(i.type_label, (counts.get(i.type_label) ?? 0) + 1));
    return [["All", items.length] as const, ...Array.from(counts.entries()).sort((a, b) => a[0].localeCompare(b[0]))];
  }, [items]);

  const [tab, setTab] = useState(0);
  const [q, setQ] = useState("");
  const current = items.find((i) => String(i.id) === String(value ?? ""));

  // On open, focus the current item's type tab and clear the search.
  useEffect(() => {
    if (!open) return;
    setQ("");
    const idx = current ? tabs.findIndex((t) => t[0] === current.type_label) : 0;
    setTab(idx > 0 ? idx : 0);
  }, [open]);

  const activeType = tabs[tab]?.[0] ?? "All";
  const needle = q.trim().toLowerCase();
  const filtered = items
    .filter((i) => (activeType === "All" || i.type_label === activeType)
      && (!needle || i.name.toLowerCase().includes(needle) || (i.vendor_code ?? "").toLowerCase().includes(needle)))
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>Select item</DialogTitle>
      <DialogContent dividers sx={{ pt: 1 }}>
        <TextField
          autoFocus fullWidth size="small" placeholder="Search items…" value={q}
          onChange={(e) => setQ(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ mb: 1 }}
        />
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={{ minHeight: 36, mb: 1 }}>
          {tabs.map(([label, count]) => (
            <Tab key={label} label={`${label} (${count})`} sx={{ minHeight: 36, textTransform: "none" }} />
          ))}
        </Tabs>
        <List dense sx={{ height: "45vh", overflow: "auto" }}>
          {filtered.map((i) => (
            <ListItemButton
              key={i.id} selected={String(i.id) === String(value ?? "")}
              onClick={() => onSelect(i.id)}
            >
              <ListItemText
                primary={i.name}
                secondary={i.vendor_code ? `${i.type_label} · vendor code ${i.vendor_code}` : i.type_label}
                primaryTypographyProps={{ fontSize: 14 }} secondaryTypographyProps={{ fontSize: 11 }} />
            </ListItemButton>
          ))}
          {filtered.length === 0 && (
            <Typography color="text.secondary" sx={{ p: 2 }}>No items match “{q}”.</Typography>
          )}
        </List>
      </DialogContent>
      <DialogActions>
        {value && <Button color="inherit" onClick={() => onSelect(null)}>Clear</Button>}
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
}

// Debounce a fast-changing value (search box) so we don't query on every keypress.
function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

// Project cell/field: shows the coded project ("C41742 — Boardwalk Acura") and
// opens a server-searched picker. Stores the project's job internal id.
// `cache` resolves an already-coded id to its label; `onResolve` feeds newly
// seen projects back into that shared cache.
// `fieldLabel` renders the trigger as a first-class outlined form field (read-only
// TextField) so it lines up with the header's other inputs — same height, border,
// floating label and focus ring. Without it, the compact button is used for table cells.
function ProjectPicker({ value, onChange, cache, onResolve, placeholder = "Select project…", fieldLabel }: {
  value: number | string | null | undefined;
  onChange: (id: number | null, opt: ProjectOption | null) => void;
  cache: Map<string, ProjectOption>;
  onResolve: (opts: ProjectOption[]) => void;
  placeholder?: string;
  fieldLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const key = value == null || value === "" ? "" : String(value);
  const current = key ? cache.get(key) : undefined;
  const label = current ? current.label : key ? `Project ${key}` : placeholder;

  return (
    <>
      {fieldLabel ? (
        <TextField
          label={fieldLabel} size="small" fullWidth value={current ? current.label : key ? `Project ${key}` : ""}
          placeholder={placeholder} onClick={() => setOpen(true)}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            readOnly: true,
            sx: { cursor: "pointer" },
            endAdornment: <InputAdornment position="end"><SearchIcon fontSize="small" color="action" /></InputAdornment>,
          }}
          inputProps={{ sx: { cursor: "pointer", textOverflow: "ellipsis" } }}
        />
      ) : (
        <Button
          variant="outlined" size="small" fullWidth onClick={() => setOpen(true)}
          sx={{ textTransform: "none", justifyContent: "flex-start", fontSize: 13, color: key ? "text.primary" : "text.secondary", borderColor: "divider" }}
        >
          <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
            {label}
          </Box>
        </Button>
      )}
      <ProjectPickerDialog
        open={open} value={value} onResolve={onResolve}
        onClose={() => setOpen(false)}
        onSelect={(opt) => { onChange(opt ? opt.id : null, opt); setOpen(false); }}
      />
    </>
  );
}

// Modal project picker: a search box that queries NetSuite projects by number
// or customer name (server-side — there are ~40k), plus a scrollable result list.
function ProjectPickerDialog({ open, value, onClose, onSelect, onResolve }: {
  open: boolean; value: number | string | null | undefined;
  onClose: () => void; onSelect: (opt: ProjectOption | null) => void;
  onResolve: (opts: ProjectOption[]) => void;
}) {
  const [q, setQ] = useState("");
  const debounced = useDebounced(q, 250);
  const ready = debounced.trim().length >= 2;

  const { data: results = [], isFetching } = useQuery({
    queryKey: ["project_search", debounced],
    queryFn: () => searchProjects(debounced),
    enabled: open && ready,
    staleTime: 30_000,
  });

  useEffect(() => { if (open) setQ(""); }, [open]);
  useEffect(() => { if (results.length) onResolve(results); }, [results]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>Select project</DialogTitle>
      <DialogContent dividers sx={{ pt: 1 }}>
        <TextField
          autoFocus fullWidth size="small" placeholder="Search by project number or customer…" value={q}
          onChange={(e) => setQ(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ mb: 1 }}
        />
        <List dense sx={{ height: "45vh", overflow: "auto" }}>
          {results.map((p) => (
            <ListItemButton
              key={p.id} selected={String(p.id) === String(value ?? "")}
              onClick={() => onSelect(p)}
            >
              <ListItemText
                primary={p.label} secondary={p.entityid}
                primaryTypographyProps={{ fontSize: 14 }} secondaryTypographyProps={{ fontSize: 11 }} />
            </ListItemButton>
          ))}
          {!ready && (
            <Typography color="text.secondary" sx={{ p: 2 }}>Type at least 2 characters to search.</Typography>
          )}
          {ready && !isFetching && results.length === 0 && (
            <Typography color="text.secondary" sx={{ p: 2 }}>No projects match “{debounced}”.</Typography>
          )}
          {ready && isFetching && (
            <Stack alignItems="center" sx={{ p: 2 }}><CircularProgress size={20} /></Stack>
          )}
        </List>
      </DialogContent>
      <DialogActions>
        {value && <Button color="inherit" onClick={() => onSelect(null)}>Clear</Button>}
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
}

// Badge color for the matched PO: the only state that blocks is Pending Receipt.
function poStatusColor(po: PoStatus): "warning" | "success" | "info" | "default" {
  if (po.pending_receipt) return "warning";
  if (po.status === "F") return "success";
  if (po.status === "D" || po.status === "E") return "info";
  return "default";
}

function poStatusTooltip(po: PoStatus): string {
  const when = po.checked_at ? dayjs(po.checked_at).format("MMM D, h:mm A") : null;
  const where = po.source === "netsuite" ? "checked in NetSuite" : "from the nightly NetSuite sync";
  return [po.error, when ? `${where} ${when}` : where].filter(Boolean).join(" · ");
}

// Blocking-error check computed live from the on-screen draft, mirroring the
// server Validator's blocking rules. Server validation only refreshes on a save
// round-trip, so relying on it made a just-fixed field still read as missing;
// this recomputes as the reviewer types. Warnings stay server-side (they need
// the NetSuite mirror). The submit endpoint re-validates regardless.
function computeBlockingErrors(content: BillDraftContent, netsuiteVendorId: number | null): string[] {
  const errors: string[] = [];
  const h = content.header ?? {};
  const lines = content.lines ?? [];
  const blank = (v: unknown) => v == null || v === "";

  if (netsuiteVendorId == null) errors.push("No vendor resolved for this bill.");
  if (blank(h.trandate)) errors.push("Date is required.");
  if (blank(h.class_id)) errors.push("Class is required.");
  if (blank(h.location_id)) errors.push("Location is required.");
  if (blank(h.approver_id)) errors.push("Approver is required.");
  if (lines.length === 0) errors.push("No lines.");
  lines.forEach((l, i) => {
    const n = i + 1;
    if (blank(l.amount)) errors.push(`Line ${n}: amount is missing.`);
    if ((l.line_type ?? "item") === "expense") {
      if (blank(l.account_id)) errors.push(`Expense line ${n}: no account assigned.`);
    } else if (blank(l.item_id)) {
      errors.push(`Line ${n}: no item assigned.`);
    }
  });
  const invoice = content.totals?.amount;
  if (blank(invoice)) {
    errors.push("Invoice total is blank. Enter the total from the PDF.");
  } else {
    const sum = lines.reduce((s, l) => s + (Number(l.amount) || 0), 0);
    const gap = Math.abs(sum - Number(invoice));
    if (gap > TIE_OUT_TOLERANCE) {
      errors.push(`Bill lines total ${fmtUsd(sum)} but the invoice total is ${fmtUsd(Number(invoice))} ` +
        `(${fmtUsd(gap)} apart). Edit the lines to match the invoice.`);
    }
  }
  return errors;
}

// Server Validator errors computeBlockingErrors also produces.
// Validator#po_variance's blocking message, which "Post anyway" can override.
const VARIANCE_BLOCK_RULE = /over PO .*Post anyway with a reason\.$/;

const LIVE_ERROR_RULE = new RegExp(
  "^(No vendor resolved|Date is required|Class is required|Location is required|Approver is required|No lines\\.|" +
  "Line \\d+: amount is missing|Expense line \\d+: no account assigned|Line \\d+: no item assigned|" +
  "Invoice total is blank|Bill lines total)",
);

// Validator::TIE_OUT_TOLERANCE: slack for tax rounded per line vs once on the total.
const TIE_OUT_TOLERANCE = 1;

// Authoritative NetSuite vendor selector. The OCR'd vendor_name is only a label;
// the bill posts against netsuite_vendor_id, set once at OCR time and previously
// not editable — so a wrong/blank match left the reviewer stuck (no way to
// repoint it, and "No vendor resolved" blocks posting). Server-side fuzzy search
// over the mirror, busiest vendors first.
function VendorPicker({ vendorId, ocrName, onChange }: {
  vendorId: number | null;
  ocrName?: string | null;
  onChange: (opt: VendorOption | null) => void;
}) {
  const [input, setInput] = useState("");
  const debounced = useDebounced(input, 250);
  const { data: options = [], isFetching } = useQuery({
    queryKey: ["bill_vendor_search", debounced],
    queryFn: () => searchVendors(debounced),
    enabled: debounced.trim().length >= 2,
    staleTime: 30_000,
  });
  const { data: resolved = [] } = useQuery({
    queryKey: ["bill_vendor_by_id", vendorId],
    queryFn: () => fetchVendorsByIds([vendorId!]),
    enabled: vendorId != null,
    staleTime: 60_000,
  });

  const selected = vendorId != null ? (resolved.find((v) => String(v.id) === String(vendorId)) ?? null) : null;
  // Keep the current vendor in the option list so its label renders before any search.
  const merged: VendorOption[] =
    selected && !options.some((o) => String(o.id) === String(vendorId)) ? [selected, ...options] : options;
  const needsPick = vendorId == null;
  const showOcr = ocrName && ocrName.trim() !== "" && ocrName !== selected?.name;

  return (
    <Autocomplete<VendorOption>
      size="small" fullWidth options={merged} value={selected}
      getOptionLabel={(o) => o.name ?? ""}
      isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
      onChange={(_, opt) => onChange(opt)}
      onInputChange={(_, v, reason) => reason === "input" && setInput(v)}
      filterOptions={(x) => x}  // server already filtered — don't re-filter locally
      loading={isFetching}
      noOptionsText={debounced.trim().length < 2 ? "Type to search NetSuite vendors…" : "No vendors"}
      renderInput={(p) => (
        <TextField
          {...p} label="Vendor" required error={needsPick}
          helperText={needsPick
            ? (ocrName ? `Invoice reads “${ocrName}” — pick the matching NetSuite vendor.` : "Pick the NetSuite vendor.")
            : (showOcr ? `Invoice reads “${ocrName}”.` : undefined)}
        />
      )}
    />
  );
}

function DraftForm({ draft, reference, onChanged }: {
  draft: VendorBillDraft; reference?: ReferenceData; onChanged: () => void;
}) {
  const [content, setContent] = useState<BillDraftContent>(draft.draft);
  const [poNumber, setPoNumber] = useState(draft.draft.header?.po_number ?? "");
  // Received yet? NetSuite won't bill a Pending-Receipt PO, so the reviewer sees
  // that next to the PO # instead of a 400 after clicking Create Bill.
  const [poStatus, setPoStatus] = useState<PoStatus | null>(draft.matched_po ?? null);
  // The NetSuite entity the bill posts to. Lives on the record (not in draft),
  // so tracked separately and repointable via the vendor picker below.
  const [netsuiteVendorId, setNetsuiteVendorId] = useState<number | null>(draft.netsuite_vendor_id);
  useEffect(() => {
    setContent(draft.draft);
    setNetsuiteVendorId(draft.netsuite_vendor_id);
    setPoStatus(draft.matched_po ?? null);
  }, [draft.updated_at]);

  // Shared cache of project id -> option so an already-coded header/line renders
  // its label without a per-cell fetch. Seeded from the draft below and topped
  // up by each search (ProjectPicker -> registerProjects).
  const [projectCache, setProjectCache] = useState<Map<string, ProjectOption>>(new Map());
  const registerProjects = useCallback((opts: ProjectOption[]) => {
    setProjectCache((prev) => {
      const next = new Map(prev);
      let changed = false;
      opts.forEach((o) => { if (!next.has(String(o.id))) { next.set(String(o.id), o); changed = true; } });
      return changed ? next : prev;
    });
  }, []);

  // Resolve the labels for any project ids already on the draft (header + lines).
  useEffect(() => {
    const ids = new Set<string>();
    const hp = content.header.project_internal_id;
    if (hp != null && hp !== "") ids.add(String(hp));
    (content.lines ?? []).forEach((l) => {
      if (l.customer_id != null && l.customer_id !== "") ids.add(String(l.customer_id));
    });
    const missing = Array.from(ids).filter((id) => !projectCache.has(id));
    if (missing.length === 0) return;
    fetchProjectsByIds(missing).then(registerProjects).catch(() => {});
  }, [content.header.project_internal_id, content.lines, projectCache, registerProjects]);

  const posted = draft.status === "posted";
  const validation = draft.validation;
  const lineSum = useMemo(
    () => (content.lines ?? []).reduce((s, l) => s + (Number(l.amount) || 0), 0),
    [content.lines],
  );
  // Recomputed live as the reviewer edits (see computeBlockingErrors), so a
  // just-fixed field clears immediately instead of after a save round-trip.
  const liveErrors = useMemo(() => computeBlockingErrors(content, netsuiteVendorId), [content, netsuiteVendorId]);
  // Server blocks the live check can't compute (they need the NetSuite mirror).
  // Ones it can are left to liveErrors, which is current while the saved
  // validation still lists a field the reviewer has since fixed.
  const serverErrors = (validation?.errors ?? []).filter((e) => !LIVE_ERROR_RULE.test(e));
  // The over-PO block (Validator#po_variance) that "Post anyway" answers with a reason.
  const varianceBlocked = serverErrors.some((e) => VARIANCE_BLOCK_RULE.test(e));
  // Received open POs on this bill's project: what "Post stand-alone anyway" overrides.
  const standaloneBlockers = (validation?.open_pos ?? []).filter((po) => po.kind === "project" && po.received);

  const [snack, setSnack] = useState<{ msg: string; sev: "success" | "info" | "warning" | "error" } | null>(null);

  // Live "received yet?" check against NetSuite (the badge otherwise reflects the
  // nightly mirror). Once ops receives the PO this is how the reviewer moves on.
  const refreshPo = useMutation({
    mutationFn: () => refreshPoStatus(draft.id),
    onSuccess: (status) => {
      setPoStatus(status);
      if (status.error) setSnack({ msg: status.error, sev: "warning" });
      else if (status.pending_receipt) setSnack({ msg: `NetSuite still shows PO ${status.tranid ?? ""} as Pending Receipt.`, sev: "warning" });
      else setSnack({ msg: `PO ${status.tranid ?? ""} is ${status.status_name} — ready to bill.`, sev: "success" });
    },
    onError: (e) => setSnack({
      msg: (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? "Couldn't check the PO status.",
      sev: "error",
    }),
  });
  const poBlocked = !!poStatus?.pending_receipt;

  const save = useMutation({
    // pull: true matches the PO # box; a string matches that PO (an open PO the
    // server listed, "Use this PO").
    mutationFn: (pull?: boolean | string) =>
      updateBillDraft(draft.id, content, typeof pull === "string" ? pull : pull ? poNumber : undefined, netsuiteVendorId),
    onSuccess: (data, pull) => {
      onChanged();
      if (pull && data.po_match) {
        const po = data.po_match.po ?? null;
        setPoStatus(data.po_match.matched ? po : null);
        if (!data.po_match.matched) {
          setSnack({ msg: `No open PO “${data.po_match.requested}” found for this vendor in NetSuite.`, sev: "info" });
        } else if (po?.pending_receipt) {
          setSnack({ msg: `Matched PO ${data.po_match.tranid} — pulled ${data.po_match.lines} line(s), but it has not been received yet (see below).`, sev: "warning" });
        } else {
          setSnack({ msg: `Matched PO ${data.po_match.tranid} — pulled ${data.po_match.lines} line(s).`, sev: "success" });
        }
      } else if (!pull) {
        setSnack({ msg: "Draft saved.", sev: "success" });
      }
    },
    onError: () => setSnack({ msg: "Save failed.", sev: "error" }),
  });
  // Unlink the PO (e.g. one already Fully Billed by an earlier invoice) so this
  // invoice posts as a plain bill. Clearing the PO # box alone never did that:
  // the box is only read by "Match PO & Pull Lines".
  const removePo = useMutation({
    mutationFn: () => updateBillDraft(draft.id, content, undefined, netsuiteVendorId, true),
    onSuccess: () => {
      onChanged();
      setPoNumber("");
      setPoStatus(null);
      setSnack({ msg: "PO removed. This bill will post without a PO.", sev: "success" });
    },
    onError: () => setSnack({ msg: "Couldn't remove the PO.", sev: "error" }),
  });
  // "Post stand-alone anyway": the reviewer says this invoice is outside the
  // project's received open PO(s). Recorded server-side and shown to the approver.
  const postStandalone = useMutation({
    mutationFn: () => updateBillDraft(draft.id, content, undefined, netsuiteVendorId, false, true),
    onSuccess: () => {
      onChanged();
      setSnack({ msg: "Marked to post stand-alone. The approver will see the open PO(s).", sev: "success" });
    },
    onError: () => setSnack({ msg: "Couldn't save the stand-alone choice.", sev: "error" }),
  });
  // "Post anyway" over the PO variance: the reviewer gives a reason, which goes
  // in the bill memo and in front of the approver.
  const [varianceDialog, setVarianceDialog] = useState(false);
  const [varianceReason, setVarianceReason] = useState("");
  const postOverVariance = useMutation({
    mutationFn: () => updateBillDraft(draft.id, content, undefined, netsuiteVendorId, false, false, varianceReason.trim()),
    onSuccess: () => {
      onChanged();
      setVarianceDialog(false);
      setVarianceReason("");
      setSnack({ msg: "Override saved. The reason goes in the memo and to the approver.", sev: "success" });
    },
    onError: () => setSnack({ msg: "Couldn't save the override.", sev: "error" }),
  });
  // "Post bill" must persist the on-screen edits FIRST: /submit posts the
  // server-saved draft, so without this save the reviewer's latest changes
  // (department, approver, every field) were silently dropped. Save then submit.
  // Duplicate comparison dialog. "submit" mode carries the post-anyway button;
  // "compare" mode (from the banner) is look-only.
  const [dupDialog, setDupDialog] = useState<{
    mode: "submit" | "compare"; candidates: DuplicateCandidate[]; status: DuplicateCheckStatus;
  } | null>(null);
  const knownDuplicates = draft.duplicate_candidates ?? [];

  const createBill = useMutation({
    // confirmed: the candidates the reviewer compared in the dialog, or null
    // for a normal submit.
    mutationFn: async (confirmed: DuplicateCandidate[] | null) => {
      const saved = await updateBillDraft(draft.id, content, undefined, netsuiteVendorId);
      // The save re-runs the duplicate check when a key field changed. A
      // blocking match (strong, carried over after an invoice # edit, or any
      // match on a bill with no invoice #) needs a compare-and-confirm, so open
      // the dialog instead of round-tripping a 409. Other possible matches
      // don't block; they stay in the banner.
      if (!confirmed && blockingCandidates(saved.duplicate_candidates, saved.draft.header?.invoice_number).length > 0) {
        return { kind: "compare", draft: saved } as const;
      }
      return { kind: "posted", draft: await submitBillDraft(draft.id, confirmed ?? undefined) } as const;
    },
    onSuccess: (result) => {
      onChanged();
      if (result.kind === "compare") {
        setDupDialog({
          mode: "submit", candidates: result.draft.duplicate_candidates ?? [],
          status: result.draft.duplicate_check_status ?? null,
        });
        return;
      }
      setDupDialog(null);
      // Pop the freshly-posted bill open in NetSuite right away. Best-effort:
      // if a popup blocker eats it, the success banner keeps the same link.
      if (result.draft.netsuite_url) window.open(result.draft.netsuite_url, "_blank", "noopener,noreferrer");
    },
    onError: (e) => {
      // The save and the submit both stored a fresh validation (e.g. a received
      // open PO on the project), so refetch: the "Fix before posting" choices
      // are built from it and otherwise never appear after a failed post.
      onChanged();
      // The server's fresh pre-submit check found something the saved draft
      // didn't know about yet (a bill keyed in NetSuite a minute ago).
      const dup = possibleDuplicateFrom(e);
      if (dup) {
        setDupDialog({ mode: "submit", candidates: dup.duplicate_candidates, status: dup.duplicate_check_status });
      } else {
        // Any other refusal (validation, a received open PO, PO not received)
        // is fixed on the page: its error and the "Use PO" / "Post stand-alone
        // anyway" choices sit behind the full-screen dialog, so leaving it open
        // made "post anyway" look dead while every retry hit the same 422.
        setDupDialog(null);
      }
    },
  });
  const createBillError = createBill.isError && !possibleDuplicateFrom(createBill.error)
    ? ((createBill.error as { response?: { data?: { error?: string } } })?.response?.data?.error ?? "Submit failed.")
    : null;

  // "Use PO" / "Post stand-alone anyway" for the project's received open POs.
  // Either one answers a failed post's error, so it clears that error.
  const standaloneActions = standaloneBlockers.length > 0 && !posted && (
    <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
      {standaloneBlockers.filter((po) => po.tranid).map((po) => (
        <Button key={po.netsuite_id} size="small" variant="outlined" color="inherit" sx={{ textTransform: "none" }}
          disabled={save.isPending}
          onClick={() => { createBill.reset(); setPoNumber(po.tranid!); save.mutate(po.tranid!); }}>
          Use PO {po.tranid}
        </Button>
      ))}
      <Button size="small" variant="text" color="inherit" sx={{ textTransform: "none" }}
        disabled={postStandalone.isPending || save.isPending}
        onClick={() => {
          if (window.confirm("Post this invoice stand-alone, not against the open PO(s)? The approver will see this choice.")) {
            createBill.reset();
            postStandalone.mutate();
          }
        }}>
        Post stand-alone anyway
      </Button>
    </Stack>
  );

  const [lineTab, setLineTab] = useState(0); // 0 = Items, 1 = Expenses

  const setHeader = (k: keyof BillDraftContent["header"], v: string | number | null) =>
    setContent((c) => ({ ...c, header: { ...c.header, [k]: v } }));
  // A term with a net-days count moves the due date to bill date + days; one
  // without (e.g. "15th of Month") leaves the due date for the reviewer.
  const setTerms = (id: number | null) => {
    const days = reference?.terms.find((t) => t.id === id)?.days;
    setContent((c) => {
      const header = { ...c.header, terms_id: id };
      if (days != null && c.header.trandate) header.due_date = dayjs(c.header.trandate).add(days, "day").format("YYYY-MM-DD");
      return { ...c, header };
    });
  };
  // Snackbar note when a project change re-codes Class/Location.
  const notifyProjectCoding = (coding: ProjectCoding, scope: string) => {
    const parts: string[] = [];
    if (coding.class_id != null) parts.push(`class ${coding.class_name ?? coding.class_id}`);
    if (coding.location_id != null) parts.push(`location ${coding.location_name ?? coding.location_id}`);
    if (parts.length) setSnack({ msg: `Set ${parts.join(" & ")} for ${scope} from the project.`, sev: "info" });
  };

  // A header project applies to the whole bill: record it on the header, code
  // every line's customer_id to it, then re-derive Class (from the project
  // family) + Location (from the project's market) for the header AND all lines.
  // Per-line pickers can still override an individual line afterwards.
  const setHeaderProject = async (opt: ProjectOption | null) => {
    if (opt) registerProjects([opt]);
    setContent((c) => ({
      ...c,
      header: {
        ...c.header,
        project_internal_id: opt ? opt.id : null,
        project_entityid: opt ? opt.entityid : null,
        project_id: opt ? opt.entityid : null,
      },
      lines: (c.lines ?? []).map((l) => ({ ...l, customer_id: opt ? opt.id : null })),
    }));
    if (!opt) return;
    try {
      const coding = await deriveProjectCoding(draft.id, opt.entityid);
      const cls = coding.class_id;
      const loc = coding.location_id;
      if (cls == null && loc == null) return;
      setContent((c) => {
        const header = { ...c.header };
        const linePatch: Partial<BillLine> = {};
        if (cls != null) { header.class_id = cls; linePatch.class_id = cls; }
        if (loc != null) { header.location_id = loc; linePatch.location_id = loc; }
        return { ...c, header, lines: (c.lines ?? []).map((l) => ({ ...l, ...linePatch })) };
      });
      notifyProjectCoding(coding, "all lines");
    } catch { /* leave existing coding as-is on lookup failure */ }
  };
  const setKind = (k: "vendor_bill" | "bill_credit") => setContent((c) => ({ ...c, transaction_kind: k }));
  // Repoint the bill to a NetSuite vendor. Also mirror the name into the header
  // so the label/payload stay consistent; the id is what actually posts.
  const setVendor = (opt: VendorOption | null) => {
    setNetsuiteVendorId(opt ? opt.id : null);
    if (opt) setHeader("vendor_name", opt.name);
  };
  const setLine = (i: number, k: keyof BillLine, v: string | number | null) =>
    setContent((c) => {
      const lines = [...c.lines];
      lines[i] = { ...lines[i], [k]: v };
      return { ...c, lines };
    });
  const patchLine = (i: number, patch: Partial<BillLine>) =>
    setContent((c) => {
      const lines = [...c.lines];
      lines[i] = { ...lines[i], ...patch };
      return { ...c, lines };
    });
  // Changing a single line's project re-codes just that line: its customer_id,
  // plus a re-derived Class (project family) and Location (project market).
  const applyLineProject = async (i: number, opt: ProjectOption | null) => {
    patchLine(i, { customer_id: opt ? opt.id : null });
    if (!opt) return;
    registerProjects([opt]);
    try {
      const coding = await deriveProjectCoding(draft.id, opt.entityid);
      const patch: Partial<BillLine> = {};
      if (coding.class_id != null) patch.class_id = coding.class_id;
      if (coding.location_id != null) patch.location_id = coding.location_id;
      if (Object.keys(patch).length) patchLine(i, patch);
      notifyProjectCoding(coding, `line ${i + 1}`);
    } catch { /* leave existing coding as-is on lookup failure */ }
  };
  const addLine = (line_type: "item" | "expense") =>
    setContent((c) => ({ ...c, lines: [...(c.lines ?? []), { line_type, description: "", amount: 0 }] }));
  const removeLine = (i: number) => setContent((c) => ({ ...c, lines: c.lines.filter((_, idx) => idx !== i) }));

  if (posted) {
    return (
      <Alert
        severity="success"
        action={draft.netsuite_url ? (
          <Button
            color="inherit"
            size="small"
            endIcon={<OpenInNewIcon />}
            component="a"
            href={draft.netsuite_url}
            target="_blank"
            rel="noopener noreferrer"
          >
            View bill in NetSuite
          </Button>
        ) : undefined}
      >
        Bill posted to NetSuite — internal id {draft.netsuite_bill_id}
        {draft.netsuite_tranid ? ` (${draft.netsuite_tranid})` : ""}.
        {draft.source_pdf_attached ? " Source PDF attached to the bill." : ""}
      </Alert>
    );
  }

  const items = reference?.items ?? [];
  const kind = (content.transaction_kind ?? draft.transaction_kind) as "vendor_bill" | "bill_credit";
  const allRows = (content.lines ?? []).map((line, index) => ({ line, index }));
  const itemRows = allRows.filter(({ line }) => (line.line_type ?? "item") !== "expense");
  const expenseRows = allRows.filter(({ line }) => line.line_type === "expense");
  const req = { required: true } as const;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Stack spacing={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <ToggleButtonGroup
            size="small" exclusive color="primary" value={kind}
            onChange={(_, v) => v && setKind(v)}
          >
            <ToggleButton value="vendor_bill" sx={{ textTransform: "none" }}>Vendor Bill</ToggleButton>
            <ToggleButton value="bill_credit" sx={{ textTransform: "none" }}>Bill Credit</ToggleButton>
          </ToggleButtonGroup>
          <Stack direction="row" spacing={1} alignItems="center">
            {draft.triage_decision_id != null && <RemindMeButton decisionId={draft.triage_decision_id} />}
            {draft.source_page_start != null && (
              <Chip size="small" variant="outlined" color="info"
                label={`invoice · pp. ${draft.source_page_start}–${draft.source_page_end}`} />
            )}
            <Chip size="small" label={draft.status} />
          </Stack>
        </Stack>

        {liveErrors.length > 0 && (
          <Alert severity="error">
            <strong>Fix before posting:</strong>
            <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>{liveErrors.map((e) => <li key={e}>{e}</li>)}</ul>
          </Alert>
        )}
        {knownDuplicates.length > 0 && (
          <Alert
            severity={knownDuplicates.some((c) => c.match === "strong" || c.match === "overlap") ? "error" : "warning"}
            action={
              <Button color="inherit" size="small"
                onClick={() => setDupDialog({ mode: "compare", candidates: knownDuplicates, status: draft.duplicate_check_status ?? null })}>
                Compare
              </Button>
            }
          >
            <strong>Possible duplicate.</strong>{" "}
            {knownDuplicates.length} existing bill{knownDuplicates.length === 1 ? "" : "s"} for this vendor
            {" "}look{knownDuplicates.length === 1 ? "s" : ""} like this one
            {" "}({knownDuplicates.map((c) => `${c.tranid ?? "no invoice #"} · ${fmtUsd(c.amount)} · ${c.entered_via === "ap_triage" ? "FP Accounting" : "manual"}`).join("; ")}).
            {knownDuplicates.some((c) => c.match === "overlap") &&
              " Some of its lines repeat lines already billed on the same project."}
            {" "}Compare before posting.
          </Alert>
        )}
        {knownDuplicates.length === 0 && draft.duplicate_check_status === "check_unavailable" && (
          <Alert severity="info">
            NetSuite couldn't be searched for duplicates of this bill just now. It will be checked again when you post.
          </Alert>
        )}
        {/* Blocks that need the NetSuite mirror (open POs, contract labor without a
            job). Server-computed, so they refresh on save rather than as you type. */}
        {serverErrors.length > 0 && (
          <Alert severity="error">
            <strong>Fix before posting (checked against NetSuite on save):</strong>
            <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>{serverErrors.map((e) => <li key={e}>{e}</li>)}</ul>
            {standaloneActions}
            {varianceBlocked && !posted && (
              <Button size="small" variant="text" color="inherit" sx={{ mt: 1, textTransform: "none" }}
                disabled={postOverVariance.isPending || save.isPending}
                onClick={() => setVarianceDialog(true)}>
                Post anyway
              </Button>
            )}
          </Alert>
        )}
        <Dialog open={varianceDialog} onClose={() => setVarianceDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ pb: 1 }}>Post over the PO total</DialogTitle>
          <DialogContent dividers sx={{ pt: 1 }}>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              Say why this bill can go over the PO. The reason and your name go in the bill memo and to the approver.
            </Typography>
            <TextField autoFocus fullWidth multiline minRows={2} label="Reason" value={varianceReason}
              onChange={(e) => setVarianceReason(e.target.value)} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setVarianceDialog(false)}>Cancel</Button>
            <Button variant="contained" disabled={!varianceReason.trim() || postOverVariance.isPending}
              onClick={() => { createBill.reset(); postOverVariance.mutate(); }}>
              Save override
            </Button>
          </DialogActions>
        </Dialog>
        {validation && validation.warnings.length > 0 && (
          <Alert severity="warning">
            <ul style={{ margin: 0, paddingLeft: 18 }}>{validation.warnings.map((w) => <li key={w}>{w}</li>)}</ul>
          </Alert>
        )}

        {/* Transaction body fields. Required fields carry a red asterisk, matching NetSuite. */}
        <Stack direction="row" spacing={1}>
          <VendorPicker vendorId={netsuiteVendorId} ocrName={content.header.vendor_name} onChange={setVendor} />
          <TextField label="Reference #" size="small" fullWidth value={content.header.invoice_number ?? ""}
            onChange={(e) => setHeader("invoice_number", e.target.value)} />
        </Stack>
        <Stack direction="row" spacing={1}>
          <DatePicker
            label="Date" format="MM/DD/YYYY"
            value={content.header.trandate ? dayjs(content.header.trandate) : null}
            onChange={(v) => setHeader("trandate", v && v.isValid() ? v.format("YYYY-MM-DD") : "")}
            slotProps={{ textField: { size: "small", fullWidth: true, required: true } }}
          />
          <DatePicker
            label="Due Date" format="MM/DD/YYYY"
            value={content.header.due_date ? dayjs(content.header.due_date) : null}
            onChange={(v) => setHeader("due_date", v && v.isValid() ? v.format("YYYY-MM-DD") : "")}
            slotProps={{ textField: { size: "small", fullWidth: true } }}
          />
          {/* NetSuite payment term posted on the bill; the invoice's printed
              terms sit underneath so a mismatch is easy to spot. Picking a term
              re-works the due date from the bill date. */}
          <RefSelect label="Terms" options={reference?.terms ?? []} value={content.header.terms_id}
            onChange={setTerms}
            helperText={content.header.terms ? `invoice: ${content.header.terms}` : undefined} />
        </Stack>
        <TextField label="Memo" size="small" fullWidth multiline minRows={2} maxRows={6}
          value={content.header.memo ?? ""} onChange={(e) => setHeader("memo", e.target.value)} />

        {/* NetSuite coding — the transaction-body defaults (lines can override). */}
        <Stack direction="row" spacing={1}>
          <RefSelect label="Department" options={reference?.departments ?? []} value={content.header.department_id}
            onChange={(v) => setHeader("department_id", v)} />
          <RefSelect label="Class" {...req} options={reference?.classes ?? []} value={content.header.class_id}
            onChange={(v) => setHeader("class_id", v)}
            helperText={content.header.job_type ? `job: ${content.header.job_type}` : undefined} />
          <RefSelect label="Location" {...req} options={reference?.locations ?? []} value={content.header.location_id}
            onChange={(v) => setHeader("location_id", v)}
            helperText={content.header.branch ? `branch: ${content.header.branch}` : undefined} />
          <RefSelect label="Approver" {...req} options={reference?.employees ?? []} value={content.header.approver_id}
            onChange={(v) => setHeader("approver_id", v)} />
        </Stack>

        {/* NetSuite Project — one per bill; applies to every line (override per line below). */}
        <Box>
          <ProjectPicker
            fieldLabel="Project"
            value={content.header.project_internal_id} cache={projectCache} onResolve={registerProjects}
            onChange={(_, opt) => setHeaderProject(opt)}
            placeholder="Select project — applies to all lines…"
          />
          {content.header.project_id && !content.header.project_internal_id && (
            <Typography variant="caption" color="warning.main" sx={{ display: "block", mt: 0.5, ml: 0.25 }}>
              Read “{content.header.project_id}” from the invoice — pick the matching NetSuite project.
            </Typography>
          )}
        </Box>

        <Stack direction="row" spacing={1} alignItems="center">
          {/* Emptying the box drops an unmatched PO reference (e.g. a project
              number like COM-42228) on the next save, as reviewers expect; on
              draft 1433 the block stayed because the box was only read by
              Match PO. A matched PO still needs Remove PO to unlink its lines. */}
          <TextField label="PO #" size="small" sx={{ width: 220 }} value={poNumber}
            onChange={(e) => {
              setPoNumber(e.target.value);
              if (!e.target.value.trim() && content.matched_po_id == null && content.header.po_number) setHeader("po_number", "");
            }} />
          <Button variant="outlined" size="small" sx={{ whiteSpace: "nowrap", textTransform: "none" }}
            onClick={() => save.mutate(true)} disabled={save.isPending || !poNumber}>
            Match PO &amp; Pull Lines
          </Button>
          {poStatus && (
            <>
              <Tooltip title={poStatusTooltip(poStatus)}>
                <Chip size="small" variant="outlined" color={poStatusColor(poStatus)}
                  label={`PO ${poStatus.tranid ?? poStatus.netsuite_id} · ${poStatus.status_name}`} />
              </Tooltip>
              <Tooltip title="Check the PO status in NetSuite now">
                <IconButton size="small" onClick={() => refreshPo.mutate()} disabled={refreshPo.isPending} aria-label="Check PO status in NetSuite">
                  {refreshPo.isPending ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </>
          )}
          {/* Also for a PO reference that never matched (e.g. a project number
              like COM-42228): the Validator blocks it and names Remove PO as
              the way out, and clearing the box alone doesn't drop it. */}
          {(content.matched_po_id != null || !!content.header.po_number) && !posted && (
            <Tooltip title="Unlink this PO so the invoice posts as a plain bill. The lines stay for you to edit.">
              <Button variant="text" size="small" color="warning" sx={{ whiteSpace: "nowrap", textTransform: "none" }}
                onClick={() => removePo.mutate()} disabled={removePo.isPending || save.isPending}>
                Remove PO
              </Button>
            </Tooltip>
          )}
        </Stack>
        {poBlocked && (
          <Alert severity="warning"
            action={
              <Button color="inherit" size="small" onClick={() => refreshPo.mutate()} disabled={refreshPo.isPending}>
                Check again
              </Button>
            }>
            <strong>PO {poStatus?.tranid ?? poStatus?.netsuite_id} has not been received in NetSuite yet</strong>, so NetSuite
            won't let it be billed. Ask ops to receive the PO (enter an Item Receipt against it), then click
            “Check again” to continue.
            {poStatus?.source === "mirror" && poStatus?.checked_at
              ? ` Status as of ${dayjs(poStatus.checked_at).format("MMM D, h:mm A")}.`
              : ""}
          </Alert>
        )}

        {/* Items and Expenses subtabs, matching the NetSuite layout. */}
        <Tabs value={lineTab} onChange={(_, v) => setLineTab(v)} sx={{ minHeight: 40, borderBottom: 1, borderColor: "divider" }}>
          <Tab label={`Items (${itemRows.length})`} sx={{ minHeight: 40, textTransform: "none" }} />
          <Tab label={`Expenses (${expenseRows.length})`} sx={{ minHeight: 40, textTransform: "none" }} />
        </Tabs>

        {lineTab === 0
          ? <LinesTable kind="item" rows={itemRows} reference={reference} items={items} setLine={setLine} removeLine={removeLine} projectCache={projectCache} registerProjects={registerProjects} onLineProject={applyLineProject} />
          : <LinesTable kind="expense" rows={expenseRows} reference={reference} items={items} setLine={setLine} removeLine={removeLine} projectCache={projectCache} registerProjects={registerProjects} onLineProject={applyLineProject} />}

        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Button size="small" startIcon={<AddIcon />} onClick={() => addLine(lineTab === 0 ? "item" : "expense")}>
            {lineTab === 0 ? "Add Item" : "Add Expense"}
          </Button>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography variant="body2" color="text.secondary">Lines: {fmtUsd(lineSum)}</Typography>
            {/* Editable for when the PDF total was misread; the bill must tie to it. */}
            <TextField
              size="small" type="number" label="Invoice total" sx={{ width: 150 }}
              value={content.totals?.amount ?? ""} disabled={posted}
              error={content.totals?.amount == null || Math.abs(lineSum - Number(content.totals.amount)) > TIE_OUT_TOLERANCE}
              onChange={(e) => setContent((c) => ({
                ...c, totals: { ...c.totals, amount: e.target.value === "" ? null : Number(e.target.value) },
              }))}
            />
          </Stack>
        </Stack>

        {/* The same choices here: a failed post's error names them, and the
            "Fix before posting" box is usually scrolled out of view by now. */}
        {createBillError && <Alert severity="error">{createBillError}{standaloneActions}</Alert>}

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button variant="outlined" onClick={() => save.mutate(false)} disabled={save.isPending || createBill.isPending}>
            {save.isPending ? "Saving…" : "Save Draft"}
          </Button>
          <Button variant="contained" color="secondary"
            onClick={() => createBill.mutate(null)}
            disabled={save.isPending || createBill.isPending || liveErrors.length > 0 || poBlocked}>
            {createBill.isPending ? "Posting…" : `Post ${kind === "bill_credit" ? "bill credit" : "bill"} to NetSuite`}
          </Button>
        </Stack>

        {dupDialog && (
          <DuplicateCompareDialog
            open
            draftId={draft.id}
            content={content}
            pdfAvailable={!!draft.pdf_url}
            candidates={dupDialog.candidates}
            checkStatus={dupDialog.status}
            compareOnly={dupDialog.mode === "compare"}
            posting={createBill.isPending}
            error={dupDialog.mode === "submit" ? createBillError : null}
            onCancel={() => setDupDialog(null)}
            onConfirm={() => createBill.mutate(dupDialog.candidates)}
          />
        )}

        <Snackbar
          open={!!snack} autoHideDuration={5000} onClose={() => setSnack(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          {snack ? <Alert severity={snack.sev} variant="filled" onClose={() => setSnack(null)}>{snack.msg}</Alert> : undefined}
        </Snackbar>
      </Stack>
    </LocalizationProvider>
  );
}
